import React, { useState, useRef, useEffect } from 'react';
import { generateId } from '../../../utils/generateId';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AddPhotoAlternate as AddPhotoIcon,
} from '@mui/icons-material';
import { ContentBlock, ImageGridItem } from '../../../types/blocks';
import { mediaService } from '../../../services/media.service';

interface ImageGridBlockEditorProps {
  block: ContentBlock;
  onChange: (block: ContentBlock) => void;
}

function GridImageSlot({
  image,
  onUpdate,
  onRemove,
}: {
  image: ImageGridItem;
  onUpdate: (updates: Partial<ImageGridItem>) => void;
  onRemove: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isExternalUrl = image.content && /^https?:\/\/.+/.test(image.content);
  const isS3Key = image.content && image.content.startsWith('media/');

  useEffect(() => {
    if (isS3Key) {
      mediaService.resolveMediaUrl(image.content).then(setPreviewUrl);
    } else if (isExternalUrl) {
      setPreviewUrl(image.content);
    } else {
      setPreviewUrl('');
    }
  }, [image.content, isS3Key, isExternalUrl]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      const { uploadUrl, key } = await mediaService.presignUpload(
        file.name,
        file.type,
        file.size,
      );
      await mediaService.uploadToS3(uploadUrl, file, setUploadProgress);
      onUpdate({ content: key });
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const hasPreview = !!previewUrl;

  return (
    <Box sx={{ position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, bgcolor: 'background.paper', '&:hover': { color: 'error.main' } }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>

      {hasPreview ? (
        <Box sx={{ borderRadius: 1, overflow: 'hidden', maxHeight: 120, display: 'flex', justifyContent: 'center', bgcolor: '#f8f9fa' }}>
          <img
            src={previewUrl}
            alt={image.alt || 'Preview'}
            style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            minHeight: 80,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}
          onClick={() => setShowUrlInput(!showUrlInput)}
        >
          <AddPhotoIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">
            Upload or paste URL
          </Typography>
        </Box>
      )}

      {uploading && (
        <Box sx={{ mt: 0.5 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">
            {uploadProgress}%
          </Typography>
        </Box>
      )}
      {uploadError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {uploadError}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          sx={{ fontSize: '0.6875rem', textTransform: 'none', py: 0, px: 0.75, minWidth: 'auto' }}
        >
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>

      {(showUrlInput || (!hasPreview && !uploading)) && (
        <TextField
          fullWidth
          size="small"
          placeholder="Image URL (https://...)"
          value={isS3Key ? '' : image.content}
          onChange={(e) => onUpdate({ content: e.target.value })}
          sx={{ mt: 0.5 }}
          slotProps={{ htmlInput: { style: { fontSize: '0.75rem', padding: '4px 8px' } } }}
        />
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Caption"
        value={image.caption || ''}
        onChange={(e) => onUpdate({ caption: e.target.value })}
        sx={{ mt: 0.5 }}
        slotProps={{ htmlInput: { style: { fontSize: '0.75rem', padding: '4px 8px' } } }}
      />
    </Box>
  );
}

const ImageGridBlockEditor: React.FC<ImageGridBlockEditorProps> = ({ block, onChange }) => {
  const columns: number = block.meta?.columns || 3;
  const images: ImageGridItem[] = block.meta?.images || [];

  const updateImage = (index: number, updates: Partial<ImageGridItem>) => {
    const newImages = [...images];
    newImages[index] = { ...newImages[index], ...updates };
    onChange({ ...block, meta: { ...block.meta, images: newImages } });
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange({ ...block, meta: { ...block.meta, images: newImages } });
  };

  const addImage = () => {
    const newImage: ImageGridItem = {
      id: generateId(),
      content: '',
    };
    onChange({ ...block, meta: { ...block.meta, images: [...images, newImage] } });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">Columns</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={columns}
          onChange={(_, val) => { if (val) onChange({ ...block, meta: { ...block.meta, columns: val } }); }}
        >
          <ToggleButton value={2} sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1 }}>2</ToggleButton>
          <ToggleButton value={3} sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1 }}>3</ToggleButton>
          <ToggleButton value={4} sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1 }}>4</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(columns, 4)}, 1fr)`, gap: 1 }}>
        {images.map((img, index) => (
          <GridImageSlot
            key={img.id}
            image={img}
            onUpdate={(updates) => updateImage(index, updates)}
            onRemove={() => removeImage(index)}
          />
        ))}
      </Box>

      {images.length < columns && (
        <Button
          size="small"
          startIcon={<AddPhotoIcon sx={{ fontSize: 16 }} />}
          onClick={addImage}
          sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}
        >
          Add Image
        </Button>
      )}
    </Box>
  );
};

export default ImageGridBlockEditor;
